import { useEffect } from 'react';

const DEFAULT_TITLE = 'Precious Data | Subject Access Requests for Care Leavers';
const DEFAULT_DESCRIPTION = document.querySelector('meta[name="description"]')?.content || '';

/**
 * Sets the page <title>, meta description and canonical link for a public
 * page (guides, in particular, need these for search engines — this is a
 * CRA app with no server-side rendering, but Google indexes titles/meta
 * set on initial render via JS). Restores the site default on unmount so
 * navigating between pages never leaks one guide's title into another.
 */
export function useDocumentMeta({ title, description, path }) {
  useEffect(() => {
    const previousTitle = document.title;
    if (title) document.title = title;

    let descriptionTag = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionTag?.getAttribute('content') ?? null;
    if (description) {
      if (!descriptionTag) {
        descriptionTag = document.createElement('meta');
        descriptionTag.setAttribute('name', 'description');
        document.head.appendChild(descriptionTag);
      }
      descriptionTag.setAttribute('content', description);
    }

    let canonicalTag = document.querySelector('link[rel="canonical"]');
    const hadCanonicalTag = Boolean(canonicalTag);
    const previousCanonical = canonicalTag?.getAttribute('href') ?? null;
    if (path) {
      if (!canonicalTag) {
        canonicalTag = document.createElement('link');
        canonicalTag.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalTag);
      }
      canonicalTag.setAttribute('href', `https://preciousdata.org.uk${path}`);
    }

    return () => {
      document.title = previousTitle || DEFAULT_TITLE;
      if (descriptionTag) descriptionTag.setAttribute('content', previousDescription ?? DEFAULT_DESCRIPTION);
      if (canonicalTag && !hadCanonicalTag) canonicalTag.remove();
      else if (canonicalTag && previousCanonical) canonicalTag.setAttribute('href', previousCanonical);
    };
  }, [title, description, path]);
}
